import { Users, Trophy, Target, TrendingUp, BarChart3, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'

interface DuoStatsData {
  player1_id: string
  player1_name: string
  player2_id: string
  player2_name: string
  games_together: number
  wins: number
  losses: number
  win_rate: number
  average_points_per_game: number
  total_points_scored: number
  total_points_conceded: number
}

interface DuoStatsProps {
  stats: DuoStatsData
}

export function DuoStats({ stats }: DuoStatsProps) {
  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`
  const formatNumber = (value: number) => value.toLocaleString()

  if (stats.games_together === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2" />
          Duo Performance
        </h3>
        <div className="text-center text-gray-500 py-8">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="font-medium">{stats.player1_name} & {stats.player2_name}</p>
          <p className="text-sm">No games played together yet</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <Users className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{stats.player1_name} & {stats.player2_name}</h2>
              <p className="text-gray-600">{stats.games_together} games together</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/players/${stats.player1_id}`}
              className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
            >
              {stats.player1_name}
              <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
            <Link
              to={`/players/${stats.player2_id}`}
              className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
            >
              {stats.player2_name}
              <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Trophy className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercentage(stats.win_rate)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Target className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Points Scored</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.total_points_scored)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-red-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Points Conceded</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.total_points_conceded)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Avg Points/Game</p>
              <p className="text-2xl font-bold text-gray-900">{stats.average_points_per_game.toFixed(1)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Together</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{stats.wins}</p>
            <p className="text-sm text-gray-600 mt-1">Wins</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">{stats.losses}</p>
            <p className="text-sm text-gray-600 mt-1">Losses</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">
              {stats.total_points_scored > 0 && stats.total_points_conceded > 0
                ? (stats.total_points_scored / stats.total_points_conceded).toFixed(2)
                : '0.00'}
            </p>
            <p className="text-sm text-gray-600 mt-1">Points Ratio</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">
              {((stats.total_points_scored - stats.total_points_conceded) / stats.games_together).toFixed(1)}
            </p>
            <p className="text-sm text-gray-600 mt-1">Avg Point Diff</p>
          </div>
        </div>

        {/* Win Rate Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Win Rate</span>
            <span className="font-medium">{formatPercentage(stats.win_rate)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(stats.win_rate * 100, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  )
}
